import { Ability } from "@casl/ability";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import env from "../config/env";
import {
  User,
  VideoCategory,
  VimeoVideoCategory,
  Settings,
  VimeoVideo,
  Email,
  Comunication,
  GenericObjective,
  Activity,
  Reservation,
  Evaluation,
  EvaluationObjective,
  Planning,
} from "../types";

export const toUser = (data: any): User => {
  return {
    id: data.id,
    email: data.email,
    nominative: data.nominative,
    name: data.name,
    surname: data.surname,
    imageUrl: data.image_url ?? undefined,
    role: data.role ? data.role : null,
    phone: data.phone,
    contact_emergency: data.contact_emergency,
    fb: data.fb,
    ig: data.ig,
    contact_preference: data.contact_preference,
    assistance_type: data.assistance_type,
    address: data.address,
    zip_code: data.zip_code,
    city: data.city,
    state: data.state,
    province_state: data.province_state,
    gender: data.gender,
    birth_date: data.birth_date,
    picture: data.picture,
    periodic_evaluation: data.periodic_evaluation,
    note: data.note,
    createdAt: new Date(data.created_at),
    stripeID: data.stripe_id,
    subscriptionID: data.subscription_id,
    signupCompleted: !!data.signupCompleted,
    subscription_status: data.subscription_status,
    subscription_activation_date: data.subscription_activation_date
      ? new Date(data.subscription_activation_date)
      : undefined,
    subscription_disactivation_date: data.subscription_disactivation_date
      ? new Date(data.subscription_disactivation_date)
      : undefined,
    subscription_type: data.subscription_type,
  };
};

export type UserRole = {
  uid: string;
  name: string;
  acls: Ability;
};

export const toUserRole = (data: any): UserRole => {
  return {
    uid: data.uid,
    name: data.name,
    acls: new Ability(data.acls),
  };
};

/**
 * Function that sets stripe up using the correct API key.
 */

export const getStripe = () => {
  let stripePromise: Promise<Stripe | null>;

  stripePromise = loadStripe(
    env.NODE_ENV === "development" ? env.STRIPE_KEY : env.STRIPE_KEY
  );

  return stripePromise;
};

/**
 * Function that, given an hash url, retrieves all the params in that url.
 * @param {string} search - The hash url. es: window.location.hash
 * @returns { [key: string]: string }  - Returns an object containing all the params
 */
export const getUrlParams = (search: string): { [key: string]: string } => {
  let hashes = search.slice(search.indexOf("?") + 1).split("&");
  return hashes.reduce((params, hash) => {
    let [key, val] = hash.split("=");
    return Object.assign(params, { [key]: decodeURIComponent(val) });
  }, {});
};
export const toVimeoVideoCategory = (data: any): VimeoVideoCategory => {
  return {
    name: data.name,
    projectId: data.project_id,
    resourceKey: data.resource_key,
    uri: data.uri,
  };
};

export const toVideoCategory = (data: any): VideoCategory => {
  return {
    id: data.id,
    name: data.name,
    externalId: data.externalId,
    resourceKey: data.resourceKey,
    title: data.title,
    description: data.description,
    order: data.order,
    enabled: data.enabled,
  };
};

export const toSettings = (data: any): Settings => {
  return {
    key: data.key,
    value: data.value,
  };
};

export const toCustomer = (data: any): User => {
  return {
    id: data.id,
    email: data.email,
    nominative: data.nominative,
    name: data.name,
    surname: data.surname,
    imageUrl: data.image_url ?? undefined,
    role: data.role ? data.role : null,
    phone: data.phone,
    contact_emergency: data.contact_emergency,
    fb: data.fb,
    ig: data.ig,
    contact_preference: data.contact_preference,
    assistance_type: data.assistance_type,
    address: data.address,
    zip_code: data.zip_code,
    city: data.city,
    state: data.state,
    province_state: data.province_state,
    gender: data.gender,
    birth_date: data.birth_date,
    picture: data.picture,
    periodic_evaluation: data.periodic_evaluation,
    note: data.note,
    createdAt: new Date(data.created_at),
    stripeID: data.stripe_id,
    subscriptionID: data.subscription_id,
    signupCompleted: data.signupCompleted,
    subscription_activation_date: data.subscription_activation_date,
    subscription_disactivation_date: data.subscription_disactivation_date,
    subscription_type: data.subscription_type,
    subscription_status: data.subscription_status,
  };
};

export const toVimeoVideo = (data: any): VimeoVideo => {
  let picture = "";
  try {
    picture = data.pictures?.sizes
      ? data.pictures.sizes.find((size: any) => size.width === 1280).link
      : "";
  } catch (e) {}
  return {
    duration: data.duration,
    link: data.link,
    name: data.name,
    picture,
    type: data.type,
    resourceKey: data.resource_key,
  };
};

export const toEmail = (data: any): Email => {
  return {
    body: data.body,
    data: new Date(data.data),
    id: data.id,
    object: data.object,
    userId: data.userId,
    username: data.username,
  };
};

export const toCommunication = (data: any): Comunication => {
  return {
    comunicationDirection: data.comunication_direction,
    data: new Date(data.data),
    duration: data.duration,
    id: data.id,
    note: data.note,
    operator: data.operator,
    type: data.type,
    userId: data.userId,
  };
};

export const toGenericObjective = (data: any): GenericObjective => {
  return {
    checked: data.checked,
    data: data.data,
    description: data.description,
    id: data.id,
    title: data.title,
    userId: data.userId,
  };
};

export const toActivity = (data: any): Activity => {
  return {
    date: data.date,
    dateTime: new Date(data.dateTime),
    eventMeta: data.eventMeta,
    id: data.id,
    type: data.type,
    userId: data.userId,
    externalId: data.externalId,
    objectType: "activity",
  };
};

export const toReservation = (data: any): Reservation => {
  return {
    source: data.source,
    dateTime: new Date(data.dateTime),
    eventMeta: data.eventMeta,
    id: data.id,
    type: data.type,
    userId: data.userId,
    externalId: data.externalId,
    objectType: "reservation",
  };
};
export const toEvaluationObjective = (data: any): EvaluationObjective => {
  return {
    objective: toGenericObjective(data.genericObjective),
    valutation: data.valutation,
    comment: data.comment,
  };
};

export const toEvaluation = (data: any): Evaluation => {
  return {
    id: data.id,
    weight: data.weight,
    lean_mass: data.lean_mass,
    breast: data.breast || "",
    waist: data.waist || "",
    thigh: data.thigh,
    data: data.data,
    userId: data.userID,
    photo_url: data.photo_url ? data.photo_url : null,
    objectives: data.evaluationOnGenericObjective?.map(
      (objective: EvaluationObjective) => toEvaluationObjective(objective)
    ),
    personalObjective: data.personalObjective,
  };
};

export const toPlanning = (data: any): Planning => {
  return {
    id: data.id,
    dateTime: new Date(data.dateTime),
    type: data.type,
    userId: data.userId,
  };
};
